import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Box,
  Button,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import CustomPDFViewer from "src/Shared/Components/CustomPDFViewer/CustomPDFViewer";
import { useModal, useStore } from "src/Shared/Hooks";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import { SLA_CONFIG } from "../SLAManagement.config";
import { useStyles } from "./DocumentTab.styles";

import { SLAStatus } from "src/Modules/Company/Enums";
import { ClientStatus } from "src/Modules/Company/Enums/ClientStatus.enum";
import { ISLADocument } from "src/Modules/Company/Interfaces/ISLAInfo";
import UploadDocumentDialog from "./UploadDocumentDialog/UploadDocumentDialog";

interface DocumentsTabProps {
  currentStatus: number;
  showSectionErrors?: boolean;
  onCurrentDocumentChange?: (hasCurrentDocument: boolean) => void;
  onDocumentsChange?: (documents: ISLADocument[]) => void;
  expiryDate?: string;
  onSyncExpiryDate?: (expiryDate: string | null) => void;
  onRemoveDocument?: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  currentStatus,
  showSectionErrors = true,
  onCurrentDocumentChange,
  onDocumentsChange,
  expiryDate,
  onSyncExpiryDate,
  onRemoveDocument
}) => {
  const classes = useStyles();
  const modal = useModal();

  const {
    rootStore: {
      companyStore: { slaInfo, companyOverview },
    },
  } = useStore();

  const [currentDocument, setCurrentDocument] =
    useState<ISLADocument | null>(null);
  const [previousDocuments, setPreviousDocuments] = useState<
    ISLADocument[]
  >([]);

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<"current" | "previous">(
    "current",
  );


  useEffect(() => {
    const documents = (slaInfo?.slaDocuments || []) as ISLADocument[];

    const current = documents.find((x) => x.isCurrent) || null;
    const previous = documents.filter((x) => !x.isCurrent);
    setCurrentDocument(current);
    setPreviousDocuments(previous);
  }, [slaInfo]);

  useEffect(() => {
    onCurrentDocumentChange?.(!!currentDocument);
  }, [currentDocument]);

  useEffect(() => {
    const allDocuments = [
      ...(currentDocument ? [{ ...currentDocument, isCurrent: true }] : []),
      ...previousDocuments.map((x) => ({ ...x, isCurrent: false })),
    ];

    onDocumentsChange?.(allDocuments);
  }, [currentDocument, previousDocuments, onDocumentsChange]);

  const currentStatusConfig = SLA_CONFIG[currentStatus];
  const canManageDocuments = currentStatusConfig.docs !== "hidden";
  const isCurrentSla = currentStatus === SLAStatus.CurrentSLA;
  const showCurrentDocumentError =
    !!showSectionErrors && isCurrentSla && !currentDocument && companyOverview?.clientStatusID !== ClientStatus.Prospect;

  const handleView = (doc: ISLADocument) => {
    if (doc?.attachmentUrl) {
      modal.open(<CustomPDFViewer url={doc.attachmentUrl} />);
    } else if (doc.file) {
      const url = URL.createObjectURL(doc.file);
      modal.open(<CustomPDFViewer url={url} />);
    }
  };

  const openCurrentUpload = () => {
    if (!canManageDocuments) return;
    setUploadMode("current");
    setOpenUploadModal(true);
  };

  const openPreviousUpload = () => {
    if (!canManageDocuments) return;
    setUploadMode("previous");
    setOpenUploadModal(true);
  };

  const handleUploadedDocument = (newDocument: ISLADocument) => {
    if (uploadMode === "current") {
      if (currentDocument) {
        setPreviousDocuments((prev) => [
          { ...currentDocument, isCurrent: false },
          ...prev,
        ]);
      }
      setCurrentDocument(newDocument);

      // ✅ Sync the expiry date to the main form
      if (newDocument.expiryDate && onSyncExpiryDate) {
        onSyncExpiryDate(newDocument.expiryDate);
      }
    } else {
      setPreviousDocuments((prev) => [...prev, newDocument]);
    }
    setOpenUploadModal(false);
  };
  const closeUploadModal = () => {
    setOpenUploadModal(false);
  };

  const handleRemoveDocument = () => {
    if (currentDocument) {
      setPreviousDocuments((prev) => [
        { ...currentDocument, isCurrent: false },
        ...prev,
      ]);
    }
    setCurrentDocument(null);
    onRemoveDocument?.();
  };

  const getUploadedMeta = (document: any) => {
    const uploadedOn =
      document?.uploadedOn || document?.attachment?.uploadedOn || new Date();

    const size =
      document?.size ||
      document?.attachment?.size ||
      document?.attachment?.fileSize ||
      "-";

    return {
      uploadedText: uploadedOn
        ? DateTimeUtils.dateToLocalDateZone(uploadedOn)
        : "-",
      size,
    };
  };

  return (
    <>
      <Box className={classes.tabPanel}>
        <Typography className={classes.sectionLabel}>
          Current SLA document{" "}
          {currentStatus === SLAStatus.CurrentSLA && companyOverview?.clientStatusID !== ClientStatus.Prospect && (
            <span className={classes.requiredStar}>*</span>
          )}
        </Typography>

        {currentDocument ? (
          <Box className={classes.currentDocumentCard}>
            <Box className={classes.currentDocumentLeft}>
              <Box className={classes.currentDocumentIconBox}>
                <InsertDriveFileOutlinedIcon
                  className={classes.currentDocumentIcon}
                />
              </Box>

              <Box>
                <Typography className={classes.documentName}>
                  {currentDocument.file?.name ||
                    currentDocument.name ||
                    "Unnamed document"}
                </Typography>

                <Typography className={classes.documentMeta}>
                  Uploaded {getUploadedMeta(currentDocument).uploadedText} ·{" "}
                  {getUploadedMeta(currentDocument).size}
                </Typography>
              </Box>
            </Box>

            <Box className={classes.currentDocumentActions}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleView(currentDocument)}
              >
                View
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={openCurrentUpload}
              >
                Replace
              </Button>

              {currentStatus === SLAStatus.CurrentSLA && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveDocument}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <Box
              className={`${classes.uploadBox} ${!canManageDocuments ? classes.uploadBoxDisabled : ""
                } ${classes.currentUploadBox}`}
              onClick={openCurrentUpload}
              sx={
                showCurrentDocumentError
                  ? {
                    borderColor: "#ef5350 !important",
                    backgroundColor: "#fff5f5",
                  }
                  : undefined
              }
            >
              <CloudUploadOutlinedIcon
                className={classes.uploadIcon}
                sx={
                  showCurrentDocumentError
                    ? {
                      color: "#ef5350 !important",
                    }
                    : undefined
                }
              />

              <Typography
                className={classes.uploadText}
                sx={
                  showCurrentDocumentError
                    ? {
                      color: "#ef5350 !important",
                    }
                    : undefined
                }
              >
                + Upload SLA document{" "}
                <span className={classes.uploadDot}>·</span> PDF or DOCX up to
                10 MB
              </Typography>
            </Box>

            {showCurrentDocumentError && (
              <Typography
                sx={{
                  marginTop: 1,
                  fontSize: 13,
                  color: "#ef5350",
                }}
              >
                An SLA document is required for this status
              </Typography>
            )}
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography className={classes.sectionLabel}>Previous SLAs</Typography>

          {previousDocuments.map((document, index) => (
            <Box key={index} className={classes.previousDocumentRow}>
              <Box className={classes.previousDocumentLeft}>
                <Typography className={classes.documentName}>
                  {document?.file?.name ||
                    document?.name ||
                    "Unnamed document"}
                </Typography>
              </Box>

              <Box className={classes.previousDocumentRight}>
                <Typography className={classes.documentMeta}>
                  {document.expiryDate??""}
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleView(document)}
                >
                  View
                </Button>
              </Box>
            </Box>
          ))}

          <Box
            className={`${classes.uploadBox} ${!canManageDocuments ? classes.uploadBoxDisabled : ""
              } ${classes.previousUploadBox}`}
            onClick={openPreviousUpload}
          >
            <Typography className={classes.uploadText}>
              + Upload previous SLA
            </Typography>
          </Box>
        </Box>
      </Box>
      <UploadDocumentDialog
        open={openUploadModal}
        uploadMode={uploadMode}
        onClose={closeUploadModal}
        onUpload={handleUploadedDocument}
        expiryDate={expiryDate}
      />
    </>
  );
};

export default DocumentsTab;